/**
 * Southern Sotho catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 3.
 *
 * `dir: 'ltr'`. Sesotho at a mathematician's register. **Warrant → «Netefatso»**
 * (verification/warrant — flagged). Scientific loanwords are used where Sesotho standardly
 * borrows (logic → Lojiki, modal → Modale, heuristic, scalar, geometry). W/M grades stay Latin
 * (`::numbering-system/latn`). CLDR cardinal categories are one / other; the noun uses the
 * se-/di- class (thibelo → dithibelo). `frameActive` declares one/other plus an explicit `=0`.
 * Several abstract terms are flagged "expert-review suggested." Per INV-STABLE-ID a wrong
 * translation is a presentation concern only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const ST_CATALOG = {
    locale: 'st',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Patlisiso',
        [P2.sectionClaims]: 'Dipolelo',
        [P2.sectionIntegrity]: 'Botšepehi',
        [P2.axisWarrantLabel]: 'Netefatso',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empirikale',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Tlhaloso} interp {Manollo} testable {E ka lekwang} tested {E lekilwe} refuted {E hanyeditswe} program {Lenaneo la dipatlisiso} philos {Ya filosofi} other {Ha e tsejwe}}',
        [P2.axisModalLabel]: 'Modale',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Boemo ba matla {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'E ka batlisiswang',
        [P2.stateRefuted]: 'E hanyeditswe',
        [P2.stateOutside]: 'Kantle ho sebaka',
        [P2.verdictConflict]: 'E hanana le {constraints}',
        [P2.verdictOutside]: 'E hloka {missing}',
        [P2.lossRegisterPlain]: 'E nolofaditswe ho ya puong e tlwaelehileng — nepahalo e nngwe e siilwe.',
        [P2.lossRegisterKid]: 'E nolofaditswe bakeng sa babadi ba banyenyane — dintlha tse ngata di siilwe.',
        [P2.lossProfileDetail]: 'Profaele e felletseng ya Netefatso / Empirikale / Modale e akareditswe mona.',
        [P2.lossConflictDetail]: 'Dintlha tsa kgohlano di kgutsufaditswe ponong ena.',
        [P2.laneVerified]: 'E netefaditswe',
        [P2.laneUnverified]: 'Ha e a netefatswa',
        [P2.laneConflict]: 'Kgohlano e pakilwe',
        [P2.constraintLogic]: 'Lojiki',
        [P2.constraintLightBends]: 'Leseli le a kobeha tšimong ya matla a kgoheletso',
        [P2.constraintParallelPostulate]: 'Postulate ya mela e tšwanang',
        [P2.claimScalarGravity]: 'Tlhahiso ya scalar ya matla a kgoheletso',
        [P2.claimNonEuclidean]: 'Geometri eo e seng ya Euclid',
        [P2.claimSubstrateSr]: 'Manollo ya substrate ya kamano e kgethehileng',
        [P2.edgeExtObservation]: 'Tlhokomelo ya kantle',
        [P2.edgeResourceSample]: 'Mohlodi wa dipalo o arolelanwang',
        [P2.edgeHeuristicSample]: 'Tataiso ya heuristic',
        [P2.edgeSharedSample]: 'Motheo o arolelanwang',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Dithibelo',
        [P2.sectionEdges]: 'Mathōko',
        [P2.sectionFrame]: 'Foreimi',
        [P2.sectionExposure]: 'Ponahatso',
        [P2.statusAdoptedFormal]: 'E amohetswe (semmuso)',
        [P2.statusAdoptedInterpretive]: 'E amohetswe (ka tlhaloso)',
        [P2.statusSuspended]: 'E emisitswe',
        [P2.edgeEffectConstraint]: 'E theha thibelo (e ka hanyetsa)',
        [P2.edgeEffectResource]: 'Mohlodi — ha e na tšusumetso diqetong',
        [P2.edgeEffectHeuristic]: 'Heuristic — ha e na tšusumetso diqetong',
        [P2.edgeEffectShared]: 'Motheo o arolelanwang — ha e na tšusumetso diqetong',
        [P2.frameActive]: '{count, plural, =0 {Ha ho dithibelo tse sebetsang} one {# thibelo e sebetsang} other {# dithibelo tse sebetsang}}',
        [P2.exposureNone]: 'Ha ho ho itshetleha ka tlhaloso',
        [P2.exposureDep]: 'E itshetlehile ho {id}',
        [P2.laneTrackMachine]: 'Motjhini',
        [P2.laneTrackExpert]: 'Setsebi',
        [P2.laneTrackCommunity]: 'Setjhaba',
        [P2.expertAudited]: 'E hlahlobilwe',
        [P2.expertInProgress]: 'E ntse e tsamaya',
        [P2.expertNotReviewed]: 'Ha e a hlahlojwa',
        [P2.communityFlags]: 'Ho na le matšwao',
        [P2.communityNone]: 'Ha ho matšwao',
        [P2.controlChallenge]: 'Phephetsa',
        [P2.controlActivate]: 'Tsosa',
        [P2.controlMakeInterpretive]: 'Etsa ya tlhaloso',
        [P2.controlImportEdge]: 'Kenya mathōko',
        [P2.controlPromote]: 'Phahamisa',
        [P2.controlUndo]: 'Hlakola',
        [P2.controlRedo]: 'Etsa hape',
        [P2.controlRegister]: 'Ngodisa',
        [P2.promoteRejected]: 'Phahamiso e hannwe',
        [P2.promoteAccepted]: 'Phahamiso e amohetswe',
        [P2.promoteReasonRefutationTerminal]: 'Bopaki bo ke ke ba folisa khanyetso.',
        [P2.promoteReasonKeystone]: 'Phahamiso ya bohlokwa e hloka tshehetso ya empirikale.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Mofuta wa empirikale ha o a behwa.',
        [P2.promoteReasonModalNotFixed]: 'Boemo ba modale ha bo a behwa.',
        [P2.promoteReasonWarrantNotImproved]: 'Boemo ba netefatso ha bo a ntlafatswa.',
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=st.js.map